import { FormEvent, useEffect, useState } from "react";
import { AdminDashboard } from "./components/AdminDashboard";
import { InvitationAcceptance } from "./components/InvitationAcceptance";
import { LoginPanel } from "./components/LoginPanel";
import { ParticipantDashboard } from "./components/ParticipantDashboard";
import { ParticipantLobby } from "./components/ParticipantLobby";
import { PasswordActionPage } from "./components/PasswordActionPage";
import { ProfileSettingsPage } from "./components/ProfileSettingsPage";
import { AuthProvider, useAuth } from "./context/AuthContext";

function getRoute():
  | "home"
  | "admin"
  | "accept-invitation"
  | "join"
  | "participant"
  | "password-action"
  | "profile" {
  const params = new URLSearchParams(window.location.search);

  if (params.get("mode") === "resetPassword") {
    return "password-action";
  }

  if (window.location.hash.startsWith("#/accept-invitation")) {
    return "accept-invitation";
  }

  if (window.location.hash.startsWith("#/admin")) {
    return "admin";
  }

  if (window.location.hash.startsWith("#/join")) {
    return "join";
  }

  if (window.location.hash.startsWith("#/participant")) {
    return "participant";
  }

  if (window.location.hash.startsWith("#/profile")) {
    return "profile";
  }

  return "home";
}

function HomeRoute() {
  const [code, setCode] = useState("");

  function handleJoin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedCode = code.trim().toUpperCase();

    if (!normalizedCode) {
      return;
    }

    window.location.hash = `/join?code=${encodeURIComponent(normalizedCode)}`;
  }

  return (
    <main className="home-shell">
      <a className="home-admin-link" href="#/admin">
        Administration
      </a>

      <section className="home-dialog" aria-labelledby="home-title">
        <div className="home-primary">
          <div>
            <p className="eyebrow">Qorelia Formation</p>
            <h1 id="home-title">Accueil formation</h1>
            <p className="lead">
              Entrez la clé courte transmise par votre formateur pour rejoindre
              votre session.
            </p>
          </div>

          <form className="stack" onSubmit={handleJoin}>
            <label className="field">
              <span>Clé courte participant</span>
              <input
                autoComplete="one-time-code"
                inputMode="text"
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder="ABCDE"
                required
                type="text"
                value={code}
              />
            </label>
            <button className="button button-primary" type="submit">
              Rejoindre
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function AdminRoute() {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <main className="auth-shell">
        <section className="auth-panel">
          <p className="eyebrow">Qorelia Formation</p>
          <h1>Chargement</h1>
          <p className="lead">Vérification du compte administrateur.</p>
        </section>
      </main>
    );
  }

  return isAdmin ? <AdminDashboard /> : <LoginPanel />;
}

function ProfileRoute() {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <main className="auth-shell">
        <section className="auth-panel">
          <p className="eyebrow">Qorelia Formation</p>
          <h1>Chargement</h1>
          <p className="lead">Vérification du compte utilisateur.</p>
        </section>
      </main>
    );
  }

  return isAdmin ? <ProfileSettingsPage /> : <LoginPanel />;
}

function RoutedApp() {
  const [route, setRoute] = useState(getRoute);

  useEffect(() => {
    function handleRouteChange() {
      setRoute(getRoute());
    }

    window.addEventListener("hashchange", handleRouteChange);
    window.addEventListener("popstate", handleRouteChange);

    return () => {
      window.removeEventListener("hashchange", handleRouteChange);
      window.removeEventListener("popstate", handleRouteChange);
    };
  }, []);

  if (route === "accept-invitation") {
    return <InvitationAcceptance />;
  }

  if (route === "join") {
    return <ParticipantLobby />;
  }

  if (route === "participant") {
    return <ParticipantDashboard />;
  }

  if (route === "password-action") {
    return <PasswordActionPage />;
  }

  if (route === "admin") {
    return <AdminRoute />;
  }

  if (route === "profile") {
    return <ProfileRoute />;
  }

  return <HomeRoute />;
}

export function App() {
  return (
    <AuthProvider>
      <RoutedApp />
    </AuthProvider>
  );
}
